import { Control, Controller, FieldErrors } from "react-hook-form";
import { Masks } from "react-native-mask-input";
import { YStack } from "tamagui";

import { Grid, Header, getGridLayout } from "@/components";
import { Input, MaskedInput } from "@/components/forms/Input";
import Select from "@/components/forms/Select";
import { Heading, Paragraph } from "@/components/Typography";
import {
  GENDER_IDENTITY_OPTIONS,
  PRONOUN_OPTIONS,
  SEX_ASSIGNED_AT_BIRTH_OPTIONS,
} from "@/settings/constants";
import type { ProfileFormData } from "./ProfileForm";

interface PersonalInfoFieldsProps {
  control: Control<ProfileFormData>;
  errors: FieldErrors<ProfileFormData>;
  isIdVerified?: boolean;
}

export const PersonalInfoFields = ({
  control,
  errors,
  isIdVerified = false,
}: PersonalInfoFieldsProps) => (
  <YStack gap="$150">
    <Header gap="$100">
      <Heading size="$xl">Set up your profile</Heading>
      <Paragraph color="$textSecondary" size="$s">
        {isIdVerified
          ? "Your identity has been verified, so legal name, date of birth, and sex assigned at birth are locked. Contact support to update them."
          : "This helps us deliver safe, appropriate care."}
      </Paragraph>
    </Header>
    <Grid>
      <Controller
        control={control}
        name="firstName"
        render={({ field }) => (
          <Input.Controlled
            label="Legal first name"
            placeholder="Legal first name"
            autoComplete="given-name"
            textContentType="givenName"
            {...field}
            value={field.value ?? ""}
            errorText={errors.firstName?.message}
            readonly={isIdVerified}
            rootProps={getGridLayout(6)}
          />
        )}
      />

      <Controller
        control={control}
        name="lastName"
        render={({ field }) => (
          <Input.Controlled
            label="Legal last name"
            placeholder="Legal last name"
            autoComplete="family-name"
            textContentType="familyName"
            {...field}
            value={field.value ?? ""}
            errorText={errors.lastName?.message}
            readonly={isIdVerified}
            rootProps={getGridLayout(6)}
          />
        )}
      />

      <Controller
        control={control}
        name="preferredName"
        render={({ field }) => (
          <Input.Controlled
            label="Preferred name"
            placeholder="Preferred name"
            autoComplete="nickname"
            textContentType="nickname"
            {...field}
            value={field.value ?? ""}
            rootProps={getGridLayout(6)}
          />
        )}
      />

      <Controller
        control={control}
        name="dateOfBirth"
        render={({ field }) => (
          <MaskedInput.Controlled
            label="Date of birth"
            {...field}
            value={field.value ?? ""}
            mask={Masks.DATE_MMDDYYYY}
            placeholder="mm/dd/yyyy"
            inputMode="numeric"
            errorText={errors.dateOfBirth?.message}
            readonly={isIdVerified}
            rootProps={getGridLayout(6)}
          />
        )}
      />

      <Controller
        control={control}
        name="sexAssignedAtBirth"
        render={({ field }) => (
          <Select
            ref={field.ref}
            name={field.name}
            label="Sex assigned at birth"
            placeholder="Select option"
            items={SEX_ASSIGNED_AT_BIRTH_OPTIONS}
            value={field.value ?? ""}
            onValueChange={field.onChange}
            errorText={errors.sexAssignedAtBirth?.message}
            disabled={isIdVerified}
            {...getGridLayout(6)}
          />
        )}
      />

      <Controller
        control={control}
        name="genderIdentity"
        render={({ field }) => (
          <Select
            ref={field.ref}
            name={field.name}
            label="Gender identity"
            placeholder="Select option"
            items={GENDER_IDENTITY_OPTIONS}
            value={field.value ?? ""}
            onValueChange={field.onChange}
            {...getGridLayout(6)}
          />
        )}
      />

      <Controller
        control={control}
        name="pronouns"
        render={({ field }) => (
          <Select
            ref={field.ref}
            name={field.name}
            label="Pronouns"
            placeholder="Select option"
            items={PRONOUN_OPTIONS}
            value={field.value ?? ""}
            onValueChange={field.onChange}
            {...getGridLayout(6)}
          />
        )}
      />

      <Controller
        control={control}
        name="email"
        render={({ field }) => (
          <Input.Controlled
            label="Email address"
            autoComplete="email"
            textContentType="emailAddress"
            {...field}
            value={field.value ?? ""}
            inputMode="email"
            placeholder="email@example.com"
            errorText={errors.email?.message}
            rootProps={getGridLayout(6)}
          />
        )}
      />
    </Grid>
  </YStack>
);
